/**
 * Authentication Service
 * 
 * Provides methods for OAuth2 authentication with Google
 * Handles token acquisition, storage, and refresh
 */

import { useEffect, useState } from 'react';
import { storeAuthToken, getAuthToken, clearAuthToken } from '../utils/shared/dbOperations';

// Configuration will be imported from config.js
let OAUTH_CONFIG = null;

// In-memory storage for the current access token and expiration
let currentTokenData = null;

/**
 * Initialize the OAuth configuration
 * @param {Object} config - OAuth configuration object
 */
export const initOAuth = async (config) => {
  OAUTH_CONFIG = config;
  
  // Try to restore token from IndexedDB
  await restoreTokenFromStorage();
};

/**
 * Restore token from persistent storage
 * @returns {Promise<boolean>} True if token was restored successfully
 */
const restoreTokenFromStorage = async () => {
  console.log('Attempting to restore token from storage');
  try {
    const storedToken = await getAuthToken();
    console.log('Retrieved token from storage:', storedToken ? 'Token found' : 'No token found');
    
    if (storedToken && storedToken.access_token) {
      console.log('Token has access_token');
      // Check if token is expired
      if (storedToken.expires_at && new Date(storedToken.expires_at) > new Date()) {
        // Token is still valid
        console.log('Token is still valid, expires at:', storedToken.expires_at);
        currentTokenData = storedToken;
        console.log('Token restored to memory');
        return true;
      } else if (storedToken.refresh_token) {
        // Token is expired but we have a refresh token
        console.log('Token is expired, attempting to refresh');
        // For now, we'll just force a new token request
        // In the future, implement token refresh logic here
        await clearAuthToken();
        return false;
      } else {
        // Token is expired and we don't have a refresh token
        console.log('Token is expired, clearing it');
        await clearAuthToken();
      }
    }
    console.log('No valid token found in storage');
    return false;
  } catch (error) {
    console.error('Error restoring token from storage:', error);
    return false;
  }
};

/**
 * Load the Google Identity Services script
 * @returns {Promise} Resolves when the script is loaded
 */
const loadGoogleIdentityScript = () => {
  return new Promise((resolve, reject) => {
    // Check if the script is already loaded
    if (document.querySelector('script#google-identity')) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-identity';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(script);
  });
};

/**
 * Initialize the Google OAuth client
 * @returns {Promise} Resolves when the client is initialized
 */
const initGoogleOAuth = async () => {
  if (!OAUTH_CONFIG) {
    throw new Error('OAuth configuration not initialized. Call initOAuth first.');
  }

  await loadGoogleIdentityScript();

  return new Promise((resolve) => {
    // Wait for the Google Identity Services to be available
    const checkGoogleIdentity = () => {
      if (window.google && window.google.accounts && window.google.accounts.oauth2) {
        resolve();
      } else {
        setTimeout(checkGoogleIdentity, 100);
      }
    };
    checkGoogleIdentity();
  });
};

/**
 * Get an access token for Google Sheets API
 * @param {boolean} forceRefresh - Whether to force a token refresh
 * @returns {Promise<string>} The access token
 */
export const getAccessToken = async (forceRefresh = false) => {
  // If we already have a token and don't need to refresh, check if it's valid
  if (currentTokenData && currentTokenData.access_token && !forceRefresh) {
    // Check if token is expired
    if (currentTokenData.expires_at && new Date(currentTokenData.expires_at) > new Date()) {
      return currentTokenData.access_token;
    }
    // Token is expired, force refresh
    forceRefresh = true;
  }

  // Initialize OAuth if needed
  await initGoogleOAuth();

  // Request an access token
  const tokenClient = window.google.accounts.oauth2.initTokenClient({
    client_id: OAUTH_CONFIG.clientId,
    scope: OAUTH_CONFIG.scopes.join(' '),
    callback: (tokenResponse) => {
      if (tokenResponse.error) {
        throw new Error(`OAuth error: ${tokenResponse.error}`);
      }
      
      // Calculate expiration time (default to 1 hour from now if not provided)
      const expiresIn = tokenResponse.expires_in || 3600;
      const expiresAt = new Date(Date.now() + expiresIn * 1000).toISOString();
      
      // Store the token with expiration
      currentTokenData = {
        access_token: tokenResponse.access_token,
        expires_in: expiresIn,
        expires_at: expiresAt,
        token_type: tokenResponse.token_type || 'Bearer',
        scope: tokenResponse.scope || OAUTH_CONFIG.scopes.join(' ')
      };
    },
  });

  return new Promise((resolve, reject) => {
    try {
      // Request the token
      tokenClient.requestAccessToken({
        prompt: forceRefresh ? 'consent' : '',
      });

      // Check for the token every 100ms
      const checkToken = () => {
        if (currentTokenData && currentTokenData.access_token) {
          // Store token in IndexedDB for persistence
          storeAuthToken(currentTokenData).catch(err => 
            console.error('Error storing auth token in IndexedDB:', err)
          );
          
          resolve(currentTokenData.access_token);
        } else {
          setTimeout(checkToken, 100);
        }
      };
      checkToken();
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Clear the current access token
 */
export const clearAccessToken = async () => {
  currentTokenData = null;
  await clearAuthToken();
};

/**
 * Sign out the current user
 */
export const signOut = async () => {
  if (window.google && window.google.accounts && currentTokenData) {
    window.google.accounts.oauth2.revoke(currentTokenData.access_token, async () => {
      await clearAccessToken();
    });
  } else {
    await clearAccessToken();
  }
};

/**
 * Check if the user is currently authenticated
 * @returns {boolean} True if authenticated with a valid token
 */
export const isAuthenticated = () => {
  if (!currentTokenData || !currentTokenData.access_token) {
    return false;
  }
  
  // Check if token is expired
  if (currentTokenData.expires_at) {
    return new Date(currentTokenData.expires_at) > new Date();
  }
  
  return true;
};

/**
 * Perform an authenticated fetch request
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} The fetch response
 */
export const authenticatedFetch = async (url, options = {}) => {
  const token = await getAccessToken();
  
  // Add the authorization header
  const authOptions = {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    },
  };
  
  // Perform the fetch
  const response = await fetch(url, authOptions);
  
  // Handle 401 Unauthorized errors (token expired)
  if (response.status === 401) {
    // Try to refresh the token
    const newToken = await getAccessToken(true);
    
    // Retry the fetch with the new token
    authOptions.headers.Authorization = `Bearer ${newToken}`;
    return fetch(url, authOptions);
  }
  
  return response;
};

/**
 * React hook for authentication state
 * @returns {Object} Authentication state and methods
 */
export const useAuth = () => {
  const [isSignedIn, setIsSignedIn] = useState(false);
  
  // Update the signed-in state when the component mounts
  useEffect(() => {
    // Check if authenticated and update state
    const checkAuth = async () => {
      // First check in-memory state
      if (isAuthenticated()) {
        setIsSignedIn(true);
        return;
      }
      
      // If not in memory, try to restore from storage
      const restored = await restoreTokenFromStorage();
      setIsSignedIn(restored);
    };
    
    checkAuth();
    
    // Set up an interval to check the authentication state
    const interval = setInterval(() => {
      setIsSignedIn(isAuthenticated());
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Sign in method
  const signIn = async () => {
    try {
      await getAccessToken(true);
      setIsSignedIn(true);
      return true;
    } catch (error) {
      console.error('Sign in error:', error);
      return false;
    }
  };
  
  // Sign out method
  const handleSignOut = async () => {
    try {
      await signOut();
      setIsSignedIn(false);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };
  
  return {
    isSignedIn,
    signIn,
    signOut: handleSignOut,
  };
};

export default {
  initOAuth,
  getAccessToken,
  clearAccessToken,
  signOut,
  isAuthenticated,
  authenticatedFetch,
  useAuth,
};
